import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import Button from './Button';
import CTAHandler from './CTAHandler';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from '../hooks/useTranslation';
import { EXTERNAL_LINKS } from '../constants/links';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  // 判断是否在首页
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const scrollThreshold = 100; // 滚动阈值
      
      // 计算滚动进度 (0-1)
      const progress = Math.min(scrollPosition / scrollThreshold, 1);
      setScrollProgress(progress);
      setIsScrolled(scrollPosition > 20);
    };

    // 只在首页监听滚动事件
    if (isHomePage) {
      // 首页时，根据当前滚动位置设置初始状态
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // 非首页时，设置为已滚动状态（显示深色导航栏）
      setIsScrolled(true);
      setScrollProgress(1);
    }
  }, [isHomePage]);

  // 导航菜单项
  const navigationItems = [
    { name: t('nav.home'), href: '/', id: 'home' },
    { name: t('nav.product'), href: '/product', id: 'product' },
    { name: t('nav.demo'), href: '/demo', id: 'demo' },
    { name: t('nav.solutions'), href: '/solutions', id: 'solutions' },
    { name: t('nav.cases'), href: '/cases', id: 'cases' },
    // { name: t('nav.pricing'), href: '/pricing', id: 'pricing' }, // 暂时隐藏定价板块
    // { name: t('nav.resources'), href: '/resources', id: 'resources' }, // 暂时隐藏资源板块
  ];

  const isActiveRoute = (href) => {
    return location.pathname === href;
  };

  return (
    <>
      <nav 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-out"
        style={{
          // 动态背景色和毛玻璃效果
          backgroundColor: isHomePage 
            ? `rgba(15, 23, 42, ${scrollProgress * 0.8})` // 深色背景渐变
            : 'rgba(15, 23, 42, 0.8)',
          backdropFilter: `blur(${scrollProgress * 12}px)`,
          WebkitBackdropFilter: `blur(${scrollProgress * 12}px)`,
          // 动态边框
          borderBottom: `1px solid rgba(255, 255, 255, ${scrollProgress * 0.1})`,
          // 动态阴影
          boxShadow: `0 ${scrollProgress * 4}px ${scrollProgress * 24}px rgba(0, 0, 0, ${scrollProgress * 0.15})`
        }}>
        <div className="section-container">
          <div className="flex items-center justify-between h-16 lg:h-20 relative">
            {/* Logo - 左侧 */}
            <Link to="/" className="flex-shrink-0 relative z-10">
              {/* 由于现在是深色背景，只需要一个白色Logo */}
              <img
                src="/logo.png"
                alt="DecisionAI"
                className="w-[120px] h-[45px] object-contain"
              />
            </Link>

            {/* 桌面端导航菜单 - 居中 */}
            <div className="hidden lg:flex items-center space-x-8 absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className={`text-sm font-medium transition-all duration-300 hover:scale-105 ${
                    isActiveRoute(item.href)
                      ? 'text-primary-400' // 活跃状态使用亮色
                      : 'text-white/90 hover:text-white' // 统一使用白色系
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* 右侧按钮组 */}
            <div className="flex items-center space-x-3 relative z-10">
              {/* 语言切换器 - 桌面端 */}
              <div className="hidden lg:block">
                <LanguageSwitcher
                  variant="default" // 统一使用默认样式
                />
              </div>

              {/* 登录按钮 - 桌面端显示 */}
              <Button
                variant="outline"
                size="nav"
                className="hidden lg:inline-flex border-white/20 text-white hover:bg-white/10 hover:border-white/40"
                onClick={CTAHandler.handleLogin}
              >
                {t('nav.login')}
              </Button>

              {/* 移动端菜单按钮 */}
              <button
                className="lg:hidden p-2 rounded-lg transition-colors duration-200 hover:bg-white/10"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6 text-white" />
                ) : (
                  <Bars3Icon className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 移动端菜单 */}
      <div className={`fixed inset-0 z-[60] lg:hidden transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        {/* 背景遮罩 */}
        <div
          className="absolute inset-0 bg-dark-900/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />

        {/* 菜单内容 */}
        <div className={`absolute top-0 right-0 w-80 max-w-[90vw] h-full bg-gradient-dark shadow-2xl transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="p-6 pt-20">
            {/* 移动端导航菜单 */}
            <div className="space-y-6">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block w-full text-left text-lg font-medium transition-colors duration-200 py-2 ${
                    isActiveRoute(item.href)
                      ? 'text-primary-400'
                      : 'text-white hover:text-primary-300'
                  }`}
                >
                  {item.name}
                </Link>
              ))}

              {/* 分割线 */}
              <div className="border-t border-white/20 my-6" />

              {/* 语言切换器 - 移动端 */}
              <div className="mb-4">
                <LanguageSwitcher variant="mobile" />
              </div>

              {/* 移动端按钮 */}
              <div className="space-y-4">
                <Button
                  variant="outline"
                  className="w-full justify-center"
                  onClick={() => {
                    CTAHandler.handleLogin();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {t('nav.login')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;